import { Component, HostBinding, Input, OnInit } from '@angular/core';

import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BasicService } from '../../services/basic.service';
import { SelectIonicPopoverOption } from './select-ionic-popover/select-ionic-popover-option';
import { SelectIonicPopoverComponent } from './select-ionic-popover/select-ionic-popover.component';

export interface ValueAndLabel<T> {
  value: T;
  label: string;
}

@Component({
  selector: 'app-select-ionic',
  templateUrl: './select-ionic.component.html',
  styleUrls: ['./select-ionic.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: SelectIonicComponent,
    },
  ],
})
export class SelectIonicComponent<T> implements OnInit, ControlValueAccessor {
  @Input() options: ValueAndLabel<T>[];
  @Input() required: boolean; // not used when multiple is true
  @Input() multiple: boolean;

  @HostBinding('class.disabled') disabled = false;

  displayedValue: string;

  private optionsForPopover: SelectIonicPopoverOption<T>[];

  private touched = false;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private onChange = (value: T | T[]) => {};
  private onTouched = () => {};

  constructor(private basicService: BasicService) {}

  ngOnInit() {
    this.optionsForPopover = this.options.map(option => ({ ...option }));
    if (!this.multiple && !this.required) {
      this.optionsForPopover.unshift(new SelectIonicPopoverOption<T>(null, ''));
    }
  }

  maybeOpenPopover(event) {
    if (!this.disabled) {
      this.openPopover(event);
    }
  }

  private openPopover(event) {
    const onChangeFn = (value: T | T[]) => {
      this.updateSelectedOptionAndDisplayedValue(value);
      this.markAsTouched();
      this.onChange(value);
    };
    const popoverProps = {
      options: this.optionsForPopover,
      multiple: this.multiple,
      onChangeFn,
    };
    this.basicService.popoverWithResult<T | T[]>(event, SelectIonicPopoverComponent, popoverProps);
  }

  private updateSelectedOptionAndDisplayedValue(value: T | T[]) {
    const displayedValues: string[] = [];
    for (const option of this.optionsForPopover) {
      option.isSelected = this.multiple ? (value as T[]).includes(option.value) : (value as T) === option.value;
      if (option.isSelected) {
        displayedValues.push(option.label);
      }
    }
    this.displayedValue = displayedValues.join(', ');
  }

  private markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  clear() {
    const value = this.multiple ? [] : null;
    this.writeValue(value);
    this.updateSelectedOptionAndDisplayedValue(value);
    this.onChange(value);
  }

  //
  // implementation of ControlValueAcccessor
  //

  writeValue(value: T | T[]) {
    if (value === null) {
      return;
    }
    this.updateSelectedOptionAndDisplayedValue(value);
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }
}
